import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

/**
 * Data point for the response time chart
 */
export interface ResponseTimeDataPoint {
  timestamp: Date | string;
  responseTime: number | null;
  status?: 'UP' | 'DOWN';
}

/**
 * Time range options
 */
type TimeRange = '24h' | '7d' | '30d';

/**
 * ResponseTimeChart component props
 */
interface ResponseTimeChartProps {
  data: ResponseTimeDataPoint[];
  isLoading?: boolean;
  onTimeRangeChange?: (range: TimeRange) => void;
  initialTimeRange?: TimeRange;
  height?: number;
}

/**
 * Format timestamp for X-axis based on time range
 */
function formatXAxisTick(timestamp: number, range: TimeRange): string {
  const date = new Date(timestamp);

  if (range === '24h') {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (range === '7d') {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      hour: 'numeric',
      hour12: true,
    });
  }

  // 30d
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get time range in milliseconds
 */
function getTimeRangeMs(range: TimeRange): number {
  switch (range) {
    case '24h':
      return 24 * 60 * 60 * 1000;
    case '7d':
      return 7 * 24 * 60 * 60 * 1000;
    case '30d':
      return 30 * 24 * 60 * 60 * 1000;
  }
}

/**
 * Format response time for display
 */
function formatResponseTime(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Custom tooltip component
 */
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: {
      timestamp: string | Date;
      responseTime: number | null;
      status?: 'UP' | 'DOWN';
    };
  }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0 || !payload[0]) {
    return null;
  }

  const data = payload[0].payload;
  const date = new Date(data.timestamp);

  return (
    <Paper
      sx={{
        p: 1.5,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 2,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: data.status === 'DOWN' ? '#EF4444' : '#10B981',
        }}
      >
        {formatResponseTime(data.responseTime)}
      </Typography>
      {data.status && (
        <Typography
          variant="caption"
          sx={{
            color: data.status === 'DOWN' ? '#EF4444' : '#10B981',
          }}
        >
          Status: {data.status}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Loading skeleton for the chart
 */
function ChartSkeleton({ height }: { height: number }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Skeleton variant="text" width={120} height={24} />
        <Skeleton variant="rounded" width={200} height={32} />
      </Box>
      <Skeleton variant="rounded" height={height - 80} />
    </Paper>
  );
}

/**
 * ResponseTimeChart component
 * Displays response times over time using a line chart
 */
export function ResponseTimeChart({
  data,
  isLoading = false,
  onTimeRangeChange,
  initialTimeRange = '24h',
  height = 300,
}: ResponseTimeChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [timeRange, setTimeRange] = useState<TimeRange>(initialTimeRange);

  // Handle time range change
  const handleTimeRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRange: TimeRange | null
  ) => {
    if (newRange !== null) {
      setTimeRange(newRange);
      onTimeRangeChange?.(newRange);
    }
  };

  // Calculate time domain for the chart (always show full selected range)
  const timeDomain = useMemo(() => {
    const now = Date.now();
    const rangeMs = getTimeRangeMs(timeRange);
    return [now - rangeMs, now] as [number, number];
  }, [timeRange]);

  // Process data for chart
  const chartData = useMemo(() => {
    return data.map((point) => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }));
  }, [data]);

  // Calculate statistics
  const stats = useMemo(() => {
    const validData = data.filter((d) => d.responseTime !== null);
    if (validData.length === 0) {
      return { avg: null, min: null, max: null };
    }

    const responseTimes = validData.map((d) => d.responseTime as number);
    return {
      avg: Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length),
      min: Math.min(...responseTimes),
      max: Math.max(...responseTimes),
    };
  }, [data]);

  if (isLoading) {
    return <ChartSkeleton height={height} />;
  }

  const hasData = data.length > 0;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Response Time
          </Typography>
          {hasData && stats.avg !== null && (
            <Typography variant="caption" color="text.secondary">
              Avg: {formatResponseTime(stats.avg)} | Min: {formatResponseTime(stats.min)} | Max:{' '}
              {formatResponseTime(stats.max)}
            </Typography>
          )}
        </Box>

        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: { xs: 1.5, sm: 2 },
              py: 0.5,
              fontSize: '0.75rem',
            },
          }}
        >
          <ToggleButton value="24h">24h</ToggleButton>
          <ToggleButton value="7d">7d</ToggleButton>
          <ToggleButton value="30d">30d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      {!hasData ? (
        <Box
          sx={{
            height: height - 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available for the selected time range
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: height - 80 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 10,
                right: isMobile ? 10 : 30,
                left: isMobile ? 0 : 10,
                bottom: 10,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
                vertical={false}
              />
              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={timeDomain}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={(value) => formatXAxisTick(value, timeRange)}
                minTickGap={isMobile ? 40 : 60}
              />
              <YAxis
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}ms`}
                width={isMobile ? 50 : 60}
                domain={['auto', 'auto']}
              />
              <Tooltip content={<CustomTooltip />} />
              {stats.avg !== null && (
                <ReferenceLine
                  y={stats.avg}
                  stroke={theme.palette.primary.main}
                  strokeDasharray="3 3"
                  strokeOpacity={0.5}
                />
              )}
              <Line
                type="monotone"
                dataKey="responseTime"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  fill: '#10B981',
                  strokeWidth: 2,
                  stroke: theme.palette.background.paper,
                }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}

export default ResponseTimeChart;
